import React, { useState, useEffect } from 'react';
import { User, Mail, Award, Shield, LogOut, Trash2, Clock, TrendingUp, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';

const BADGE_INFO = {
  first_check: { name: 'First Check', description: 'Completed your first verification', icon: '🎯' },
  security_minded: { name: 'Security Minded', description: 'Completed 10 verifications', icon: '🛡️' },
  vigilant: { name: 'Vigilant', description: 'Completed 50 verifications', icon: '👁️' },
  guardian: { name: 'Guardian', description: 'Completed 100 verifications', icon: '🏆' },
  threat_hunter: { name: 'Threat Hunter', description: 'Detected 5 threats', icon: '🎯' },
  streak_7: { name: 'Week Warrior', description: '7-day verification streak', icon: '🔥' },
  streak_30: { name: 'Monthly Master', description: '30-day verification streak', icon: '⭐' },
};

export const AccountPage = () => {
  const { user, logout, authFetch } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await authFetch('/api/verification/stats', { method: 'GET' });
        const data = response.data;
        if (data.success) {
          setStats(data.stats);
          setRecentActivity(data.recentActivity || []);
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [authFetch]);

  if (!user) {
    return null;
  }

  const displayStats = stats || user.stats;

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 sm:gap-3 mb-2">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl gradient-primary flex items-center justify-center">
            <User className="w-4 h-4 sm:w-5 sm:h-5 text-primary-foreground" />
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-bold text-foreground">My Account</h2>
        </div>
        <p className="text-sm sm:text-base text-muted-foreground">
          Manage your account and view your verification statistics
        </p>
      </div>

      {/* User Info Card */}
      <GlassCard className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img src={user.picture} alt={user.name || user.email} referrerPolicy="no-referrer"
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover border border-border/40" data-testid="account-avatar" />
            ) : (
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full gradient-primary flex items-center justify-center">
                <span className="text-2xl sm:text-3xl font-bold text-primary-foreground">
                  {user.name ? user.name[0].toUpperCase() : user.email[0].toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <h3 className="font-display text-lg sm:text-xl font-semibold text-foreground" data-testid="account-username">
                {user.name || 'VeriLink User'}
              </h3>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Mail className="w-3 h-3" />
                {user.email}
              </p>
              {user.authProvider === 'google' && (
                <span className="inline-flex items-center gap-1 mt-1 text-[11px] text-muted-foreground bg-muted/60 px-2 py-0.5 rounded-full" data-testid="account-google-chip">
                  <svg className="w-3 h-3" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
                    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z" />
                  </svg>
                  Signed in with Google
                </span>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Member since {new Date(user.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={logout}
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </GlassCard>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-2xl sm:text-3xl font-bold font-display text-primary">
              {displayStats?.totalVerifications || 0}
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">Total Checks</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-2xl sm:text-3xl font-bold font-display text-destructive">
              {displayStats?.threatsDetected || 0}
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">Threats Found</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-2xl sm:text-3xl font-bold font-display text-warning flex items-center justify-center gap-1">
              <span>🔥</span> {displayStats?.streak || 0}
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">Day Streak</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-2xl sm:text-3xl font-bold font-display text-success">
              {user.badges?.length || 0}
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">Badges</p>
          </CardContent>
        </Card>
      </div>

      {/* Verification Breakdown */}
      <GlassCard className="p-4 sm:p-6">
        <h4 className="font-display font-semibold text-lg mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          Verification Breakdown
        </h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 rounded-xl bg-muted/50">
            <Shield className="w-6 h-6 mx-auto mb-2 text-primary" />
            <div className="text-xl sm:text-2xl font-bold font-display">
              {displayStats?.linksVerified || 0}
            </div>
            <p className="text-xs text-muted-foreground">Links</p>
          </div>
          <div className="text-center p-4 rounded-xl bg-muted/50">
            <Mail className="w-6 h-6 mx-auto mb-2 text-secondary" />
            <div className="text-xl sm:text-2xl font-bold font-display">
              {displayStats?.emailsVerified || 0}
            </div>
            <p className="text-xs text-muted-foreground">Emails</p>
          </div>
          <div className="text-center p-4 rounded-xl bg-muted/50">
            <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-warning" />
            <div className="text-xl sm:text-2xl font-bold font-display">
              {displayStats?.articlesVerified || 0}
            </div>
            <p className="text-xs text-muted-foreground">Articles</p>
          </div>
        </div>
      </GlassCard>

      {/* Badges */}
      <GlassCard className="p-4 sm:p-6">
        <h4 className="font-display font-semibold text-lg mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-warning" />
          Earned Badges
        </h4>
        {user.badges && user.badges.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {user.badges.map((badgeId) => {
              const badge = BADGE_INFO[badgeId];
              if (!badge) return null;
              return (
                <div 
                  key={badgeId}
                  className="p-3 rounded-xl bg-gradient-to-br from-warning/10 to-warning/5 border border-warning/20"
                >
                  <div className="text-2xl mb-1">{badge.icon}</div>
                  <h5 className="font-semibold text-sm">{badge.name}</h5>
                  <p className="text-xs text-muted-foreground">{badge.description}</p>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Award className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>No badges earned yet</p>
            <p className="text-sm">Complete verifications to earn badges!</p>
          </div>
        )}
      </GlassCard>
    </div>
  );
};

export default AccountPage;
